<div class="legal-entity-form-container">
  <!-- Loading Spinner -->
  @if (saving) {
    <div class="loading-overlay">
      <mat-spinner diameter="60"></mat-spinner>
      <p>Saving legal entity...</p>
    </div>
  }

  <!-- Header Section -->
  <div class="header-section">
    <div class="title-section">
      <h1>{{ isEditMode ? 'Edit Legal Entity' : 'New Legal Entity' }}</h1>
      <p class="subtitle">
        {{ isEditMode ? 'Update legal entity information' : 'Create a new legal entity record' }}
        @if (currentLegalEntity.inactive) {
          <span class="inactive-badge">(INACTIVE)</span>
        }
      </p>
    </div>
    
    <div class="header-actions">
      <button mat-stroked-button color="accent" (click)="cancelSubmit()">
        <mat-icon>close</mat-icon>
        Cancel
      </button>
      <button mat-raised-button color="primary" 
              [disabled]="!canSave || !isFormValid() || saving"
              (click)="onSubmit()">
        <mat-icon>save</mat-icon>
        {{ isEditMode ? 'Update Legal Entity' : 'Save Legal Entity' }}
      </button>
    </div>
  </div>

  <form [formGroup]="form" (ngSubmit)="onSubmit()">
    
    <!-- Main Legal Entity Information Card -->
    <mat-card class="info-card">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>account_balance</mat-icon>
          Legal Entity Information
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <div class="form-grid entity-grid">
          
          <!-- Legal Entity Number (Read-only) -->
          <mat-form-field appearance="outline">
            <mat-label>Legal Entity Number</mat-label>
            <input matInput formControlName="legalEntityNum" readonly>
            <mat-icon matSuffix color="warn">lock</mat-icon>
          </mat-form-field>

          <!-- Legal Entity Name -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Legal Entity Name *</mat-label>
            <input matInput formControlName="legalEntityName" placeholder="Enter legal entity name" required>
            <mat-icon matSuffix>business</mat-icon>
            @if (form.get('legalEntityName')?.hasError('required') && submitted) {
              <mat-error>Legal Entity name is required</mat-error>
            }
            @if (form.get('legalEntityName')?.hasError('minlength') && submitted) {
              <mat-error>Name must be at least 2 characters</mat-error>
            }
          </mat-form-field>

          <!-- Legal Entity Parent -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Legal Entity Parent</mat-label>
            <input matInput formControlName="legalEntityParent" placeholder="Parent entity name">
            <mat-icon matSuffix>corporate_fare</mat-icon>
          </mat-form-field>

          <!-- FEIN -->
          <mat-form-field appearance="outline">
            <mat-label>FEIN</mat-label>
            <input matInput formControlName="fein" placeholder="12-3456789">
            <mat-icon matSuffix>badge</mat-icon>
            @if (form.get('fein')?.hasError('pattern') && submitted) {
              <mat-error>Invalid FEIN format (use 12-3456789)</mat-error>
            }
          </mat-form-field>

        </div>
      </mat-card-content>
    </mat-card>

    <!-- Address Information Card -->
    <mat-card class="info-card">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>place</mat-icon>
          Address Information
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <div class="form-grid address-grid">
          
          <!-- Address (Full width) -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Address</mat-label>
            <textarea matInput 
                      formControlName="address"
                      rows="2"
                      placeholder="Street address">
            </textarea>
            <mat-icon matSuffix>home</mat-icon>
          </mat-form-field>

          <!-- City, State, ZIP -->
          <mat-form-field appearance="outline">
            <mat-label>City</mat-label>
            <input matInput formControlName="city" placeholder="City">
            <mat-icon matSuffix>location_city</mat-icon>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>State</mat-label>
            <input matInput
                   formControlName="state"
                   placeholder="State"
                   [matAutocomplete]="stateAuto">
            <mat-icon matSuffix>map</mat-icon>
            <mat-autocomplete #stateAuto="matAutocomplete">
              @for (state of filteredStates$ | async; track state) {
                <mat-option [value]="state">{{ state }}</mat-option>
              }
            </mat-autocomplete>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>ZIP Code</mat-label>
            <input matInput formControlName="zip" placeholder="12345 or 12345-6789">
            <mat-icon matSuffix>markunread_mailbox</mat-icon>
            @if (form.get('zip')?.hasError('pattern') && submitted) {
              <mat-error>Invalid ZIP format (use 12345 or 12345-6789)</mat-error>
            }
          </mat-form-field>

        </div>
      </mat-card-content>
    </mat-card>

    <!-- Status & Comments Card -->
    <mat-card class="info-card">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>settings</mat-icon>
          Status & Notes
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        
        <!-- Active/Inactive Toggle -->
        <div class="status-section">
          <div class="toggle-container">
            <mat-slide-toggle
              formControlName="inactive"
              [disabled]="isDisabledDueToInactive"
              (change)="inactiveToggleChanged($event)">
              <span class="toggle-label" [class.active]="!isInactive" 
                                        [class.inactive]="isInactive">
                @if (!isInactive) {
                  <mat-icon>check_circle</mat-icon>
                  Active
                } @else {
                  <mat-icon>cancel</mat-icon>
                  Inactive
                }
              </span>
            </mat-slide-toggle>
          </div>
        </div>

        <mat-divider></mat-divider>

        <!-- Comments Section -->
        <div class="comments-section">
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Comments & Notes</mat-label>
            <textarea matInput 
                      formControlName="comments"
                      rows="4"
                      placeholder="Additional notes about this legal entity">
            </textarea>
            <mat-icon matSuffix>note</mat-icon>
          </mat-form-field>
        </div>

      </mat-card-content>
    </mat-card>

    <!-- Inactive Legal Entity Warning -->
    @if (savedAsInactiveInDb && currentLegalEntity.inactive) {
      <mat-card class="warning-card">
        <mat-card-content>
          <div class="warning-message">
            <mat-icon color="warn">warning</mat-icon>
            <span>You cannot save because this is an INACTIVE Legal Entity. Contact Helpdesk if you need to make changes.</span>
          </div>
        </mat-card-content>
      </mat-card>
    }

    <!-- Validation Messages -->
    @if (!isFormValid() && submitted) {
      <mat-card class="validation-card">
        <mat-card-content>
          <div class="validation-message">
            <mat-icon color="warn">warning</mat-icon>
            <span>{{ getValidationMessage() }}</span>
          </div>
        </mat-card-content>
      </mat-card>
    }

  </form>
</div>